'use strict';

/**
 * Jest / mock-HTTP stand-in for PortalApi graph DTOs. Not imported by the live tracker path.
 */

const PUBLISHING = 'Publishing';
const V7_MANIFEST = Object.freeze({
	pipeline: 'V7',
	workflow: PUBLISHING,
	parentStage: 'V7ProjectPoller',
	parentService: 'V7ProductService',
	iterationDimension: 'SampleSet',
});
const POLICY = 'allIterationsSucceeded';

/** @param {string} pipeline @param {string} stage @param {string} service */
function factMatch(pipeline, stage, service) {
	return { pipeline, workflow: PUBLISHING, stage, service };
}

/** @param {string} id @param {string} pipeline @param {string} stage @param {string} service @param {boolean} [manifest] */
function node(id, pipeline, stage, service, manifest) {
	return {
		id,
		factMatch: factMatch(pipeline, stage, service),
		...(manifest
			? { iterationManifestMatch: { ...V7_MANIFEST, pipeline }, completionPolicy: POLICY }
			: {}),
	};
}

export const MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE = Object.freeze({
	V7: {
		id: 'single-v7-project-flow',
		pipeline: 'V7',
		nodes: [
			node('v7-project-poller', 'V7', 'V7ProjectPoller', 'V7ProductService'),
			node('v7-project-available', 'V7', 'V7ProjectAvailable', 'PortalApi'),
			node('v7-sample-set-available', 'V7', 'V7SampleSetAvailable', 'V7ProductService', true),
			node('v7-product-sample-set-biovisservice', 'V7', 'ProductSampleSetAvailable', 'BioVisService', true),
			node('v7-product-sample-set-portalapi', 'V7', 'ProductSampleSetAvailable', 'PortalApi', true),
			node('v7-product-sample-set-pathwayexplorerapi', 'V7', 'ProductSampleSetAvailable', 'PathwayExplorerApi', true),
		],
	},
	Core: {
		id: 'core-sample-set-detail',
		pipeline: 'Core',
		nodes: [
			node('core-stats-completed', 'Core', 'StatsCompleted', 'ProductService'),
			node('core-portal-pssa', 'Core', 'ProductSampleSetAvailable', 'PortalApi'),
			node('core-pathway', 'Core', 'ProductSampleSetAvailable', 'PathwayExplorerApi'),
			node('core-heatmap', 'Core', 'ProductSampleSetAvailable', 'HeatmapGenerationToolApi'),
			node('core-biovis', 'Core', 'ProductSampleSetAvailable', 'BioVisService'),
		],
	},
	DiscoveryPanels: {
		id: 'discovery-panels-sample-set-detail',
		pipeline: 'DiscoveryPanels',
		nodes: [
			node('dp-stats-completed', 'DiscoveryPanels', 'StatsCompleted', 'ProductService'),
			node('dp-portal-pssa', 'DiscoveryPanels', 'ProductSampleSetAvailable', 'PortalApi'),
			node('dp-smartpanel', 'DiscoveryPanels', 'ProductSampleSetAvailable', 'SmartPanelDataService'),
			node('dp-discovery-panels', 'DiscoveryPanels', 'ProductSampleSetAvailable', 'DiscoveryPanelsApi'),
			node(
				'dp-smartpanel-viz',
				'DiscoveryPanels',
				'ProductSmartPanelVisualizationDataAvailable',
				'DiscoveryPanelsApi',
			),
		],
	},
	Kits: {
		id: 'kits-sample-set-detail',
		pipeline: 'Kits',
		nodes: [
			node('kits-manifest-available', 'Kits', 'ManifestAvailable', 'PortalApi'),
			node('kits-product-project-available', 'Kits', 'ProjectAvailable', 'ProductService'),
			node('kits-portal-project-available', 'Kits', 'ProjectAvailable', 'PortalApi'),
			node('kits-datanorm-completed', 'Kits', 'DataNormCompleted', 'ProductService'),
			node('kits-portal-pssa', 'Kits', 'ProductSampleSetAvailable', 'PortalApi'),
			node('kits-pathway', 'Kits', 'ProductSampleSetAvailable', 'PathwayExplorerApi'),
			node('kits-biovis', 'Kits', 'ProductSampleSetAvailable', 'BioVisService'),
		],
	},
	Merged: {
		id: 'merged-project-detail',
		pipeline: 'Merged',
		nodes: [
			node('merged-merge-projects', 'Merged', 'MergeProjects', 'PortalApi'),
			node('merged-project-available', 'Merged', 'ProjectAvailable', 'PortalApi'),
		],
	},
});

export const MOCK_PIPELINE_STATUS_GRAPH_CATALOG = Object.freeze({
	graphs: Object.values(MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE),
});

/** @param {unknown} pipeline */
export function mockPipelineStatusGraphForPipeline(pipeline) {
	const key = String(pipeline ?? '').trim();
	const lower = key.toLowerCase();
	if (lower === 'core') return MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE.Core;
	if (lower === 'discoverypanels' || lower === 'discovery-panels') {
		return MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE.DiscoveryPanels;
	}
	if (lower === 'kits') return MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE.Kits;
	if (lower === 'merged') return MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE.Merged;
	return MOCK_PIPELINE_STATUS_GRAPH_BY_PIPELINE.V7;
}
